import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';

const PrimaryNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [neighbors, setNeighbors] = useState([]);
  const { role, user, signOut } = useAuth();

  const navigationItems = [
    {
      label: '목표',
      labelEn: '목표',
      path: '/project-dashboard',
      icon: 'LayoutGrid',
      requiredRole: 'user',
    },
    {
      label: '피드',
      labelEn: '피드',
      path: '/feed',
      icon: 'Newspaper',
      requiredRole: 'public',
    },
    {
      label: '이웃',
      labelEn: '이웃',
      path: '/neighbors',
      icon: 'Users',
      requiredRole: 'user',
    },
    {
      label: '사람들',
      labelEn: '사람들',
      path: '/people',
      icon: 'Globe',
      requiredRole: 'user',
    },
    {
      label: '프로필',
      labelEn: '프로필',
      path: '/user-profile-dashboard',
      icon: 'User',
      requiredRole: 'user',
    },
    {
      label: '관리자',
      labelEn: '관리자',
      path: '/admin-dashboard',
      icon: 'Shield',
      requiredRole: 'admin',
    },
  ];

  const filteredNavItems = navigationItems?.filter((item) => {
    if (item?.requiredRole === 'public') return true;
    if (item?.requiredRole === 'admin') return role === 'admin';
    return !!user?.id || role === 'admin';
  });

  const mobileBottomNavItems = useMemo(
    () => (filteredNavItems || []).filter((item) => item?.path !== '/user-profile-dashboard' && item?.path !== '/admin-dashboard'),
    [filteredNavItems]
  );

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } finally {
      navigate('/login-registration');
    }
  };

  const unreadCount = useMemo(
    () => (notifications || []).filter((n) => !n?.isRead).length,
    [notifications]
  );

  const hasNewNeighborActivity = useMemo(
    () => (neighbors || []).some((n) => n?.hasNew),
    [neighbors]
  );

  const loadNeighbors = useCallback(async () => {
    if (!user?.id) {
      setNeighbors([]);
      return;
    }

    const { data, error } = await supabase.rpc('ch_list_neighbors_with_activity', {
      p_window_hours: 24,
    });

    if (error) {
      setNeighbors([]);
      return;
    }

    setNeighbors(
      (data || []).map((x) => ({
        id: x.neighbor_id,
        username: x.username,
        avatarUrl: x.avatar_url || '',
        lastActivityAt: x.last_activity_at,
        hasNew: !!x.has_new,
      }))
    );
  }, [user?.id]);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }

    const { data, error } = await supabase
      .from('ch_notifications')
      .select('id, created_at, is_read, type, proof_id, project_id, challenge_id, feed_post_id, activity_title, actor:ch_profiles!ch_notifications_actor_id_fkey(id, username, avatar_url)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30);

    if (error) {
      setNotifications([]);
      return;
    }

    setNotifications(
      (data || []).map((x) => ({
        id: x.id,
        createdAt: x.created_at,
        isRead: !!x.is_read,
        type: x.type,
        proofId: x.proof_id,
        projectId: x.project_id,
        challengeId: x.challenge_id,
        feedPostId: x.feed_post_id,
        activityTitle: x.activity_title || '',
        actor: x.actor
          ? {
            id: x.actor.id,
            username: x.actor.username,
            avatarUrl: x.actor.avatar_url || '',
          }
          : null,
      }))
    );
  }, [user?.id]);

  const markNeighborsSeen = useCallback(async () => {
    if (!user?.id) return;
    const { error } = await supabase.rpc('ch_mark_neighbors_seen');
    if (!error) {
      setNeighbors((prev) => (prev || []).map((n) => ({ ...n, hasNew: false })));
    }
  }, [user?.id]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    loadNeighbors();
  }, [loadNeighbors]);

  useEffect(() => {
    if (!user?.id) return;
    if (location?.pathname !== '/neighbors') return;
    markNeighborsSeen();
  }, [location?.pathname, markNeighborsSeen, user?.id]);

  const markAllRead = useCallback(async () => {
    if (!user?.id) return;
    const hasUnread = (notifications || []).some((n) => !n?.isRead);
    if (!hasUnread) return;

    const { error } = await supabase
      .from('ch_notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);

    if (!error) {
      setNotifications((prev) => (prev || []).map((n) => ({ ...n, isRead: true })));
    }
  }, [notifications, user?.id]);

  const toggleNotifMenu = async () => {
    setIsNotifOpen((v) => !v);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-navigation bg-card shadow-gaming-md">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center space-x-1 transition-gaming hover:opacity-80 mr-auto"
            >
              <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center">
                <img
                  src="/icon.png"
                  alt="꿈현.Labs"
                  className="w-7 h-7 object-contain"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">
                kkumhyeon.<span className="text-primary">labas</span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center space-x-2">
              {filteredNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center space-x-2 px-6 py-2 rounded-md transition-gaming ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-gaming-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <span className="relative inline-flex">
                    <Icon
                      name={item?.icon}
                      size={20}
                      color={isActivePath(item?.path) ? 'currentColor' : 'currentColor'}
                    />
                    {item?.path === '/neighbors' && hasNewNeighborActivity ? (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-destructive" />
                    ) : null}
                  </span>
                  <span className="font-medium">{item?.label}</span>
                </Link>
              ))}

              {user ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={async () => {
                      await toggleNotifMenu();
                      if (!isNotifOpen) await markAllRead();
                    }}
                    className="relative flex items-center justify-center w-10 h-10 rounded-md transition-gaming text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label="알림"
                  >
                    <Icon name="Bell" size={20} color="currentColor" />
                    {unreadCount > 0 ? (
                      <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-bold flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    ) : null}
                  </button>

                  {isNotifOpen ? (
                    <div
                      className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-gaming-lg overflow-hidden"
                      onMouseLeave={() => setIsNotifOpen(false)}
                    >
                      <div className="px-4 py-3 border-b border-border">
                        <div className="text-sm font-heading font-semibold text-foreground">알림</div>
                        <div className="text-xs text-muted-foreground caption mt-0.5">좋아요 / 이웃 활동</div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {(notifications || []).length === 0 ? (
                          <div className="px-4 py-4 text-sm text-muted-foreground caption">알림이 없습니다.</div>
                        ) : (
                          (notifications || []).map((n) => {
                            const who = n?.actor?.username || '누군가';
                            const message =
                              n?.type === 'proof_like'
                                ? `${who}님이 좋아요를 했습니다`
                                : n?.type === 'feed_post_like'
                                  ? `${who}님이 회원님의 피드 글을 좋아합니다`
                                  : n?.type === 'feed_post_comment'
                                    ? `${who}님이 회원님의 피드 글에 댓글을 남겼습니다`
                                : n?.type === 'neighbor_project_created'
                                  ? `${who}님이 프로젝트를 등록했습니다`
                                  : n?.type === 'neighbor_challenge_created'
                                    ? `${who}님이 챌린지를 등록했습니다`
                                    : n?.type === 'neighbor_proof_submitted'
                                      ? `${who}님이 인증을 등록했습니다`
                                      : `${who}님의 알림이 있습니다`;

                            const subMessage =
                              n?.type === 'feed_post_like' || n?.type === 'feed_post_comment'
                                ? n?.activityTitle
                                : n?.type === 'neighbor_project_created'
                                ? n?.activityTitle
                                : n?.type === 'neighbor_challenge_created'
                                  ? n?.activityTitle
                                  : n?.type === 'neighbor_proof_submitted'
                                    ? n?.activityTitle
                                    : '';

                            return (
                              <button
                                type="button"
                                key={n.id}
                                className="w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-gaming"
                                onClick={() => {
                                  setIsNotifOpen(false);
                                  if (n?.type === 'neighbor_project_created' && n?.projectId) {
                                    navigate(`/project-detail?id=${n.projectId}`);
                                    return;
                                  }
                                  if (n?.type === 'neighbor_challenge_created' && n?.challengeId) {
                                    navigate(`/challenge-detail?id=${n.challengeId}`);
                                    return;
                                  }
                                  if (n?.type === 'neighbor_proof_submitted' && n?.proofId) {
                                    navigate(`/proof-detail-view?id=${n.proofId}`);
                                    return;
                                  }
                                  if (n?.type === 'proof_like' && n?.proofId) {
                                    navigate(`/proof-detail-view?id=${n.proofId}`);
                                    return;
                                  }
                                  if ((n?.type === 'feed_post_like' || n?.type === 'feed_post_comment') && n?.feedPostId) {
                                    navigate(`/feed-detail?id=${n.feedPostId}`);
                                    return;
                                  }
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-muted overflow-hidden flex items-center justify-center border border-border">
                                    {n?.actor?.avatarUrl ? (
                                      <img
                                        src={n.actor.avatarUrl}
                                        alt={who}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <Icon name="User" size={18} className="text-muted-foreground" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm text-foreground truncate">
                                      {message}
                                    </div>
                                    {subMessage ? (
                                      <div className="text-xs text-muted-foreground caption mt-0.5 truncate">
                                        {subMessage}
                                      </div>
                                    ) : null}
                                    {n?.createdAt ? (
                                      <div className="text-xs text-muted-foreground caption mt-0.5">
                                        {String(n.createdAt).slice(0, 16).replace('T', ' ')}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {user ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-6 py-2 rounded-md transition-gaming text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Icon name="LogOut" size={20} color="currentColor" />
                  <span className="font-medium">로그아웃</span>
                </button>
              ) : (
                <Link
                  to="/login-registration"
                  className="flex items-center space-x-2 px-6 py-2 rounded-md transition-gaming text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Icon name="LogIn" size={20} color="currentColor" />
                  <span className="font-medium">로그인</span>
                </Link>
              )}
            </div>

            <div className="flex items-center gap-1 lg:hidden">
              {user ? (
                <button
                  type="button"
                  onClick={() => navigate('/user-profile-dashboard')}
                  className="p-2 rounded-md text-foreground hover:bg-muted transition-gaming"
                  aria-label="프로필"
                >
                  <Icon name="User" size={22} />
                </button>
              ) : null}
              <button
                onClick={handleMobileMenuToggle}
                className="p-2 rounded-md text-foreground hover:bg-muted transition-gaming"
                aria-label="Toggle mobile menu"
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-card border-t border-border">
            <div className="px-4 py-4 space-y-2">
              {filteredNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-md transition-gaming ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-gaming-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon
                    name={item?.icon}
                    size={20}
                    color={isActivePath(item?.path) ? 'currentColor' : 'currentColor'}
                  />
                  <span className="font-medium">{item?.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
      <div className="fixed bottom-0 left-0 right-0 z-navigation bg-card border-t border-border lg:hidden shadow-gaming-lg">
        <div className="flex items-center justify-around px-4 py-3">
          {mobileBottomNavItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex flex-col items-center space-y-1 px-4 py-2 rounded-md transition-gaming ${
                isActivePath(item?.path)
                  ? 'text-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="relative inline-flex">
                <Icon
                  name={item?.icon}
                  size={24}
                  color={isActivePath(item?.path) ? 'var(--color-primary)' : 'currentColor'}
                />
                {item?.path === '/neighbors' && hasNewNeighborActivity ? (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-destructive" />
                ) : null}
              </span>
              <span className="text-xs font-medium caption">{item?.labelEn}</span>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

export default PrimaryNavigation;